import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Text from "_shared/text/text";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

type FeeScheduleCategoryEntry = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

/**
 * The Team Member column of a category redline, as a text redline over the members' names.
 *
 * The other columns of a category each have a redline of their own, and the Team Member column had
 * none: a changed biller was drawn as the new name alone, with nothing to say it had changed (#6632).
 * A name is what the reader compares, so the names are what is diffed, and drawn the way the Name
 * column draws its text. An unassigned member is an absent entry, so a biller who was added reads as an added
 * name and one who was taken off reads as a struck name.
 *
 * Undefined when the row draws no change to its member, so the cell keeps its plain name, and when any
 * entry hides its member from this viewer, since a hidden member cannot be compared.
 */
export function feeScheduleTeamMemberRedline(
  categoryRedline: FeeScheduleCategoryRedline,
  memberName: (category: FeeScheduleCategoryEntry) => string,
): FieldRedline<Text> | undefined {
  const { originalEntry, revisedEntry, currentEntry } = categoryRedline;
  if ([originalEntry, revisedEntry, currentEntry].some((entry) => entry?.teamMemberIsMasked)) return undefined;

  const nameEntry = (entry: FeeScheduleCategoryEntry | null | undefined): Text | null | undefined => {
    if (entry === undefined) return undefined;
    return entry?.teamMember ? new Text(memberName(entry)) : null;
  };
  const redline = new FieldRedline<Text>({
    field: ProposalField.FeeSchedule,
    originalEntry: nameEntry(originalEntry),
    revisedEntry: nameEntry(revisedEntry),
    currentEntry: nameEntry(currentEntry),
    // Another member is another person, not an edit to a name: the old name is struck whole.
    diffWords: false,
  });
  const hasChange = redline.textChanges?.some((change) => change.diff.added || change.diff.removed);
  return hasChange ? redline : undefined;
}
